import * as React from "react";
import { Typography } from "@mui/material";
import { Theme, darken, lighten } from "@mui/material/styles";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { DateTimePicker } from "@mui/x-date-pickers";
import { FieldLabelWithHelp } from "./FieldHelpIcon";

import esLocale from "date-fns/locale/es";
import ptSaLocale from "date-fns/locale/pt-BR";
import enSaLocale from "date-fns/locale/en-US";

type InputDateTimeProps = {
  label: string;
  value: Date | null;
  helperText?: string;
  error?: boolean;
  // Aviso que no bloquea: el helperText se pinta de advertencia. Un error lo tapa.
  warning?: boolean;
  showUTCTime?: boolean;
  onChange: (value: Date | null) => void;
  autoFocus?: boolean;
  disabled?: boolean;
  hidden?: boolean;
  placeholder?: string;
  nullText?: string;
  description?: string;
};

export const InputDateTime: React.FC<InputDateTimeProps> = ({
  label,
  value,
  onChange,
  showUTCTime = false,
  helperText = "",
  error = false,
  warning = false,
  autoFocus = false,
  disabled = false,
  hidden = false,
  placeholder,
  nullText,
  description,
}) => {
  const [focused, setFocused] = React.useState(false);
  if (hidden) return null;
  const locale = {
    es: esLocale,
    pt: ptSaLocale,
    en: enSaLocale,
  };

  let utcTime = "";
  if (value !== null) {
    try {
      utcTime = `UTC ${value.toISOString().substring(11, 16)}`;
    } catch (e) {
      utcTime = "";
    }
  }

  const isEmpty = value === null || value === undefined;
  const showLabelInside = description && isEmpty && !focused;
  const baseLabel =
    nullText && isEmpty && !focused ? `${label} (${nullText})` : label;
  const displayLabel = showLabelInside ? undefined : baseLabel;
  const effectivePlaceholder = showLabelInside
    ? nullText
      ? `${label} (${nullText})`
      : label
    : placeholder;

  return (
    <LocalizationProvider
      adapterLocale={locale["es"]}
      dateAdapter={AdapterDateFns}
    >
      <div style={{ display: "flex" }}>
        <DateTimePicker
          disabled={disabled}
          label={
            <FieldLabelWithHelp
              label={displayLabel}
              description={description}
            />
          }
          value={value}
          onChange={(value: Date | null) => onChange(value)}
          slotProps={{
            textField: {
              autoFocus: autoFocus,
              size: "small",
              sx: { m: 1 },
              helperText: helperText,
              error: error,
              FormHelperTextProps:
                warning && !error
                  ? {
                      sx: {
                        color: (theme: Theme) =>
                          theme.palette.mode === "dark"
                            ? lighten(theme.palette.warning.main, 0.5)
                            : darken(theme.palette.warning.main, 0.5),
                      },
                    }
                  : undefined,
              placeholder: effectivePlaceholder,
              onFocus: () => setFocused(true),
              onBlur: () => setFocused(false),
              InputLabelProps: description ? { shrink: true } : undefined,
            },
          }}
        />
        {showUTCTime && (
          <Typography sx={{ display: "inline", pt: 2 }} variant={"h6"}>
            {utcTime}
          </Typography>
        )}
      </div>
    </LocalizationProvider>
  );
};
